// Staff data access. These run as the signed-in staff member; row-level security limits instructors
// to their assigned courses and reserves enrollments, roles and settings for admins.
import { supabase } from './supabase';
import type { Chapter, Course, EnrollmentProgress, Lesson, LessonBlock, LessonProgress, Profile } from './types';

function check<T>(res: { data: T | null; error: unknown }): T {
  if (res.error) throw res.error;
  return res.data as T;
}

export async function adminCounts() {
  const count = async (q: PromiseLike<{ count: number | null; error: unknown }>) => {
    const r = await q;
    if (r.error) throw r.error;
    return r.count ?? 0;
  };
  const [students, active, completed, published] = await Promise.all([
    count(supabase.from('profiles').select('id', { count: 'exact', head: true })),
    count(supabase.from('enrollments').select('id', { count: 'exact', head: true }).eq('status', 'active')),
    count(supabase.from('enrollments').select('id', { count: 'exact', head: true }).eq('status', 'completed')),
    count(supabase.from('courses').select('id', { count: 'exact', head: true }).eq('status', 'published')),
  ]);
  return { students, active, completed, published };
}

export interface CourseRow extends Course { enrolled: number; completed: number }
export async function listAllCourses(): Promise<CourseRow[]> {
  const courses = check(await supabase.from('courses').select('*').order('updated_at', { ascending: false })) as Course[];
  const ens = check(await supabase.from('enrollments').select('course_id,status')) as { course_id: string; status: string }[];
  return courses.map((c) => ({
    ...c,
    enrolled: ens.filter((e) => e.course_id === c.id && e.status !== 'revoked').length,
    completed: ens.filter((e) => e.course_id === c.id && e.status === 'completed').length,
  }));
}

export async function getCourse(id: string): Promise<Course | null> {
  return check(await supabase.from('courses').select('*').eq('id', id).maybeSingle());
}

export async function createCourse(title: string): Promise<Course> {
  const slug = title.toLowerCase().replace(/&/g, 'and').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60) || 'course';
  return check(await supabase.from('courses').insert({ title, slug: `${slug}-${Math.random().toString(36).slice(2, 6)}` }).select().single());
}

export async function saveCourse(id: string, patch: Partial<Course>): Promise<Course> {
  const { id: _id, updated_at: _u, ...rest } = patch;
  return check(await supabase.from('courses').update({ ...rest, published_at: rest.status === 'published' ? new Date().toISOString() : undefined }).eq('id', id).select().single());
}

export async function getCurriculumAdmin(courseId: string) {
  const [ch, ls] = await Promise.all([
    supabase.from('chapters').select('*').eq('course_id', courseId).order('position'),
    supabase.from('lessons').select('*').eq('course_id', courseId).order('position'),
  ]);
  return { chapters: check(ch) as Chapter[], lessons: check(ls) as Lesson[] };
}

export async function addChapter(courseId: string, title: string, position: number): Promise<Chapter> {
  return check(await supabase.from('chapters').insert({ course_id: courseId, title, position }).select().single());
}
export async function updateChapter(id: string, patch: Partial<Chapter>) {
  check(await supabase.from('chapters').update(patch).eq('id', id).select().single());
}
export async function deleteChapter(id: string) {
  const r = await supabase.from('chapters').delete().eq('id', id);
  if (r.error) throw r.error;
}

export async function addLesson(chapterId: string, title: string, position: number): Promise<Lesson> {
  return check(await supabase.from('lessons').insert({ chapter_id: chapterId, title, position }).select().single());
}
export async function updateLesson(id: string, patch: Partial<Lesson>) {
  const { id: _id, course_id: _c, ...rest } = patch;
  check(await supabase.from('lessons').update(rest).eq('id', id).select().single());
}
export async function deleteLesson(id: string) {
  const r = await supabase.from('lessons').delete().eq('id', id);
  if (r.error) throw r.error;
}

export async function listBlocks(lessonId: string): Promise<LessonBlock[]> {
  return check(await supabase.from('lesson_blocks').select('*').eq('lesson_id', lessonId).order('position'));
}
export async function addBlock(lessonId: string, type: LessonBlock['type'], position: number, config: Record<string, unknown>): Promise<LessonBlock> {
  return check(await supabase.from('lesson_blocks').insert({ lesson_id: lessonId, type, position, config }).select().single());
}
export async function updateBlock(id: string, patch: Partial<LessonBlock>) {
  check(await supabase.from('lesson_blocks').update({ config: patch.config, position: patch.position }).eq('id', id).select().single());
}
export async function deleteBlock(id: string) {
  const r = await supabase.from('lesson_blocks').delete().eq('id', id);
  if (r.error) throw r.error;
}

/** Swap positions of two siblings so a list keeps a stable order. */
export async function swapPositions(table: 'chapters' | 'lessons' | 'lesson_blocks', a: { id: string; position: number }, b: { id: string; position: number }) {
  const r1 = await supabase.from(table).update({ position: b.position }).eq('id', a.id);
  if (r1.error) throw r1.error;
  const r2 = await supabase.from(table).update({ position: a.position }).eq('id', b.id);
  if (r2.error) throw r2.error;
}

export interface StudentEnrollment {
  id: string; course_id: string; status: string; source: string; enrolled_at: string;
  percent_complete: number; completed_lessons: number; required_lessons: number; last_access_at: string | null;
}
export interface StudentRow extends Profile {
  enrollments: StudentEnrollment[];
}
export async function listStudents(search: string): Promise<StudentRow[]> {
  let q = supabase.from('profiles').select('*').order('full_name').limit(200);
  if (search.trim()) {
    const s = search.trim().replace(/[%,()]/g, '');
    q = q.or(`full_name.ilike.%${s}%,email.ilike.%${s}%`);
  }
  const profiles = check(await q) as Profile[];
  if (!profiles.length) return [];
  const ids = profiles.map((p) => p.id);
  const [ens, prog] = await Promise.all([
    supabase.from('enrollments').select('id,user_id,course_id,status,source,enrolled_at').in('user_id', ids),
    supabase.from('enrollment_progress').select('enrollment_id,percent_complete,completed_lessons,required_lessons,last_access_at').in('user_id', ids),
  ]);
  const progById = new Map((check(prog) as Pick<EnrollmentProgress, 'enrollment_id' | 'percent_complete' | 'completed_lessons' | 'required_lessons' | 'last_access_at'>[])
    .map((p) => [p.enrollment_id, p]));
  const rows = check(ens) as { id: string; user_id: string; course_id: string; status: string; source: string; enrolled_at: string }[];
  return profiles.map((p) => ({
    ...p,
    enrollments: rows.filter((e) => e.user_id === p.id).map((e) => {
      const pr = progById.get(e.id);
      return { ...e, percent_complete: Number(pr?.percent_complete ?? 0), completed_lessons: pr?.completed_lessons ?? 0, required_lessons: pr?.required_lessons ?? 0, last_access_at: pr?.last_access_at ?? null };
    }),
  }));
}

export async function enrollStudent(userId: string, courseId: string) {
  // Re-activates a revoked enrollment rather than duplicating it.
  const r = await supabase.from('enrollments').upsert({ user_id: userId, course_id: courseId, source: 'complimentary', status: 'active' }, { onConflict: 'user_id,course_id' });
  if (r.error) throw r.error;
}

export async function revokeEnrollment(enrollmentId: string) {
  const r = await supabase.from('enrollments').update({ status: 'revoked' }).eq('id', enrollmentId);
  if (r.error) throw r.error;
}

export interface StudentDetail {
  profile: Profile;
  enrollments: (EnrollmentProgress & { source: string; first_access_at: string | null })[];
  courses: Course[];
  chapters: Chapter[];
  lessons: Lesson[];
  progress: LessonProgress[];
}

/** Everything an admin (or the student's assigned instructor) needs to review one student. */
export async function getStudentDetail(userId: string): Promise<StudentDetail | null> {
  const profile = check(await supabase.from('profiles').select('*').eq('id', userId).maybeSingle()) as Profile | null;
  if (!profile) return null;
  const [ep, en] = await Promise.all([
    supabase.from('enrollment_progress').select('*').eq('user_id', userId),
    supabase.from('enrollments').select('id,source,first_access_at').eq('user_id', userId),
  ]);
  const extra = new Map((check(en) as { id: string; source: string; first_access_at: string | null }[]).map((e) => [e.id, e]));
  const enrollments = (check(ep) as EnrollmentProgress[]).map((e) => ({
    ...e, source: extra.get(e.enrollment_id)?.source ?? '', first_access_at: extra.get(e.enrollment_id)?.first_access_at ?? null,
  }));
  if (!enrollments.length) return { profile, enrollments, courses: [], chapters: [], lessons: [], progress: [] };
  const courseIds = enrollments.map((e) => e.course_id);
  const [c, ch, ls, lp] = await Promise.all([
    supabase.from('courses').select('*').in('id', courseIds),
    supabase.from('chapters').select('*').in('course_id', courseIds).order('position'),
    supabase.from('lessons').select('*').in('course_id', courseIds).order('position'),
    supabase.from('lesson_progress').select('*').in('enrollment_id', enrollments.map((e) => e.enrollment_id)),
  ]);
  return { profile, enrollments, courses: check(c) as Course[], chapters: check(ch) as Chapter[], lessons: check(ls) as Lesson[], progress: check(lp) as LessonProgress[] };
}
